import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getPreferredLanguage } from "../lib/theme/getPreferredLanguage";
import { create } from "zustand";
import { ISettingsConfig } from "src/types/ISettingsConfig";

export const settingsStoreId = "cai-tiler-settings";
export const languagePrefId = "cai:lang:preference";

const defaults = {
  theme: "dark",
  language: getPreferredLanguage(),
};

function getCache() {
  if (window.localStorage) {
    const currentSettings = window.localStorage.getItem(settingsStoreId);
    if (currentSettings) {
      try {
        const obj = JSON.parse(currentSettings);
        obj.language = defaults.language;
        return obj;
      } catch (e) {
        return defaults;
      }
    } else {
      return defaults;
    }
  } else {
    return defaults;
  }
}

function useLanguages() {
  const { t } = useTranslation();
  return [
    { label: t("lang.english"), value: "en-US" },
    { label: t("lang.spanish"), value: "es-ES" },
    { label: t("lang.french"), value: "fr-FR" },
    { label: t("lang.hindi"), value: "hi-IN" },
  ];
}

export const useSettingsStore = create<
  ISettingsConfig & {
    saveSettings: (_settings: Partial<ISettingsConfig>) => void;
  }
>((set) => {
  return {
    ...getCache(),
    saveSettings: (settings: ISettingsConfig) => {
      if (
        document.body.classList.contains("light") &&
        settings.theme === "dark"
      ) {
        document.body.classList.remove("light");
      }
      if (window.localStorage) {
        const { language, ...rest } = settings;
        window.localStorage.setItem(settingsStoreId, JSON.stringify(rest));
        window.localStorage.setItem(languagePrefId, language);
      }
      return set((state) => ({
        ...state,
        ...settings,
      }));
    },
  };
});

export const useSettingsState = () => {
  const languages = useLanguages();
  return useSettingsStore((state) => {
    return {
      language: state.language,
      languages,
      theme: state.theme,
    };
  });
};

export const useSettingsDispatch = () => {
  const queryClient = useQueryClient();

  return useSettingsStore((state) => {
    return (d: Partial<ISettingsConfig>) => {
      state.saveSettings({
        ...state,
        ...d,
      });
      // force re-fetch for everything to get API provided translations
      queryClient.invalidateQueries({ queryKey: ["organization"] });
    };
  });
};
